import React, { useEffect, useRef, useState } from 'react';
import { PopoverMenu, SparkIcon } from '@spark';
import {
    templateLinksForTaskType,
    acceptAttributeForTaskType,
    supportedFormatsLabelForTaskType,
} from '@/features/datasets/utils/datasetUploadFlow';
import { buildAddDataMenuItems, toPopoverMenuItems } from '@/features/datasets/utils/addDataMenuItems';
import { isAIGenerationDatasetSupported } from '@/features/datasets/utils/aiGenerationDataset';
import { isRetrievalAgentSupported } from '@/features/datasets/utils/retrievalAgentDataset';
import UploadCard from './UploadCard';

/**
 * Tiny (24px) tertiary dropdown button used in the Data Upload header
 * (Figma 3088-9109/9110): 12/18 text, 16px icons, radius 8, #e2e2e9 border.
 */
const TinyMenuButton = ({ label, startIconSlug, items }) => {
    const [open, setOpen] = useState(false);
    const wrapRef = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        const handleOutside = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handleOutside);
        return () => document.removeEventListener('mousedown', handleOutside);
    }, [open]);

    return (
        <div className="cw-dc-tiny-menu" ref={wrapRef}>
            <button type="button" className="cw-dc-tiny-btn" onClick={() => setOpen((o) => !o)}>
                {startIconSlug && <SparkIcon slug={startIconSlug} size="small" />}
                <span>{label}</span>
                <SparkIcon slug="chevron-down" size="small" />
            </button>
            {open && (
                items.some((item) => item.href) ? (
                    <div className="cw-dc-tiny-menu__panel">
                        {items.map((item) => (
                            <a
                                key={item.label}
                                className="cw-dc-tiny-menu__item"
                                href={item.href}
                                download
                                onClick={() => setOpen(false)}
                            >
                                {item.label}
                            </a>
                        ))}
                    </div>
                ) : (
                    <div className="cw-dc-tiny-menu__spark">
                        <PopoverMenu
                            tip={false}
                            items={toPopoverMenuItems(items, { onClose: () => setOpen(false) })}
                        />
                    </div>
                )
            )}
        </div>
    );
};

/**
 * "Data Upload" section of the wizard Data Collection step (Figma 3088-9104 +
 * 3001-68334): title row with "Need a Dataset?" / "Download Template" tiny
 * dropdowns, the 90/10 explainer, and the dataset/benchmark upload cards.
 *
 * `visibleTargets` scopes which cards render — used by the task-page Upload
 * Data modal to show only Dataset or only Benchmark. Default shows both.
 */
const DataUploadSection = ({
    taskType,
    isMultilabel,
    slots,
    uploadedNames,
    pickFiles,
    onRemovePending,
    onRemoveUploaded,
    onOpenLibrary,
    onOpenHuggingFace,
    onGenerateWithAi,
    onOpenRetrievalAgent,
    datasetError,
    columnErrorByTarget = {},
    onDismissColumnError,
    visibleTargets = ['dataset', 'benchmark'],
    showLibraryLink = true,
    labelsLoadingByTarget = {},
    usecaseName,
    /** Agentic task (autonomy level > 1): json/jsonl/parquet only + trajectory templates. */
    isAgentic = false,
}) => {
    const acceptAttribute = acceptAttributeForTaskType(taskType, { isAgentic });
    const formatsLabel = supportedFormatsLabelForTaskType(taskType, { isAgentic });
    const templateItems = templateLinksForTaskType(taskType, { isMultilabel, isAgentic }).map(
        (link) => ({
            label: link.text,
            href: link.href,
        })
    );
    const needDataItems = buildAddDataMenuItems({
        includeLibrary: Boolean(onOpenLibrary),
        includeHuggingFace: Boolean(onOpenHuggingFace),
        includeGenerateAi: isAIGenerationDatasetSupported({ taskType }),
        includeRetrievalAgent:
            isRetrievalAgentSupported({ taskType }) && Boolean(onOpenRetrievalAgent),
        onLibrary: () => onOpenLibrary(),
        onHuggingFace: onOpenHuggingFace,
        onGenerateAi: onGenerateWithAi,
        onRetrievalAgent: onOpenRetrievalAgent,
    });
    const targets = visibleTargets.filter(
        (target) => target === 'dataset' || target === 'benchmark'
    );

    return (
        <div className="cw-dc-upload-section">
            <div className="cw-dc-upload-section__header">
                <div className="cw-dc-upload-section__title-row">
                    <h3 className="cw-dc-section-title">Data Upload</h3>
                    <div className="cw-dc-upload-section__buttons">
                        <TinyMenuButton label="Need a Dataset?" items={needDataItems} />
                        <TinyMenuButton
                            label="Download Template"
                            startIconSlug="download"
                            items={templateItems}
                        />
                    </div>
                </div>
                <p className="cw-dc-upload-section__desc">
                When no dedicated Benchmark is provided, 90% of the Data Set is used to teach the AI, while the remaining 10% becomes the Benchmark - used as an exam to verify it actually learned to perform the task correctly.
                </p>
            </div>
            <div
                className={[
                    'cw-dc-upload-cards',
                    targets.length === 1 ? 'cw-dc-upload-cards--single' : '',
                ]
                    .filter(Boolean)
                    .join(' ')}
            >
                {targets.map((target) => (
                    <UploadCard
                        key={target}
                        target={target}
                        slot={slots[target]}
                        uploadedNames={uploadedNames?.[target] || []}
                        taskType={taskType}
                        isAgentic={isAgentic}
                        acceptAttribute={acceptAttribute}
                        formatsLabel={formatsLabel}
                        onPickFiles={(files) => pickFiles(target, files)}
                        onRemovePending={(entryId) => onRemovePending(target, entryId)}
                        onRemoveUploaded={(uploadedName) =>
                            onRemoveUploaded(target, uploadedName)
                        }
                        onOpenLibrary={() => onOpenLibrary(target)}
                        showLibraryLink={showLibraryLink}
                        error={
                            columnErrorByTarget[target] ||
                            (target === 'dataset'
                                ? datasetError
                                : targets.length === 1
                                  ? datasetError
                                  : undefined)
                        }
                        onDismissError={
                            columnErrorByTarget[target]
                                ? () => onDismissColumnError?.(target)
                                : undefined
                        }
                        labelsLoading={Boolean(labelsLoadingByTarget[target])}
                        usecaseName={usecaseName}
                    />
                ))}
            </div>
        </div>
    );
};

export default DataUploadSection;
